/* ═══════════════════════════════════════════════════════════════
   THE DESIGNER — the project-side surface.

   Microsoft Word, except it is physically incapable of producing a
   malformed spec. ONE canvas, ONE selection system, ONE document —
   and, since R5, NO MODES.

   The body always feels like writing: click anywhere and type. The
   header, footer, pre-body and post-body bands always feel like the
   forma editor: click a band, drag tokens onto it, bind placeholders,
   inline, without a mode flip. Level styling is a sub-tool you reach
   for (the Levels palette, or right-click ▸ Level style…), never
   something a click on body text triggers.

   Josh's law: MENU ACCESS ALWAYS; DOCKS ARE OPTIONAL. Since §2.8 that
   guarantee is carried by the panel BUTTONS on row 2: every panel is
   one click from its own button, which drops it down anchored, and
   the same button dragged docks or floats it. Nothing in this product
   is reachable only by docking something.

   Ctrl+Z is universal (§2.7): it undoes the last thing the user did,
   text or formatting, without them choosing a kind of undo.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Editor } from '@tiptap/react';
import { undoDepth } from '@tiptap/pm/history';
import { Download, Stamp } from 'lucide-react';
import type {
  ContentBlock,
  Issuance,
  ProjectManualIssuance,
  ProjectSpec,
} from '../../shared/types';
import { Prefs } from '../services/prefsService';
import EmptyState from '../components/EmptyState';
import { isWeb } from '../lib/platform';
import type { FormaDockHandle } from '../components/FormaDock';
import { DesignerCanvas } from './DesignerCanvas';
import {
  FindButton,
  FindReplaceHost,
  InsertImageButton,
  InsertTableButton,
  SmartBlockPicker,
} from './Tools';
import { InlineMarks, LevelCluster, UniversalHistory } from './ToolbarControls';
import { DesignerToolbar, type PanelEntry, type ToolbarItem } from './Toolbar';
import { useDesignerDoc, type SaveState } from './useDesignerDoc';
import { useFormaDraft } from './useFormaDraft';
import {
  DESIGNER_PALETTES,
  DesignerWorkspace,
  PANEL_ICONS,
  isOverDockZone,
  type FloatingTool,
} from './Workspace';
import { useUndoCoordinator } from './undoCoordinator';
import { FirmPrefs } from '../services/firmPrefsService';
import { pageVariants, type PageVariant, type StyleSelection } from './formaZonesModel';
import { resolvePlaceholders, usedPlaceholderIds } from '../../shared/forma-placeholders';
import { formatSpecNumber } from './paper';
import { blocksToDoc } from './schema';
import type { NavItem } from './Navigation';
import { IssuanceDesk, type IssueExportOptions } from './IssuanceDesk';
import { ExportDialog } from './ExportDialog';
import { RevisionStylesPanel } from './RevisionStylesPanel';
import { HistoryPanel } from './HistoryPanel';
import { exportManualSpecs, type ExportFormat } from './exportManual';
import './Designer.css';

export interface DesignerProps {
  /** The section currently open. */
  projectSpecId: string;
  /** Open a different section (the shell owns routing). */
  onOpenSection: (specId: string) => void;
  /** Leave the Designer. */
  onBack: () => void;
  /** Back to the project's Specs list, where sections are added/removed. */
  onManageSections?: () => void;
  userName: string;
  canEdit: boolean;
  /** project_specs.create_issuance — gates the Issue flow. Defaults to
   *  canEdit when the shell has no finer-grained answer. */
  canIssue?: boolean;
  /** admin_center.manage_firm_settings — gates "Set as firm default", which
   *  changes what every uncustomised colleague sees (contract §2.9). */
  canSetFirmDefault?: boolean;
  toast: (msg: string, isError?: boolean) => void;
  /** Theme, for the dockview host. */
  isDark?: boolean;
  /** Scopes the workspace layout and the toolbar to the signed-in user. */
  userId?: string;
  /** The shell opened this document to WORK ON THE PAPER (opening a project
   *  forma from the Formas library). There is no mode to switch into any
   *  more — the workspace simply brings the formatting tools forward. A new
   *  `ts` re-applies the request without remounting the surface. */
  openFormatting?: { ts: number } | null;
  /** Hands the shell a stable "save now" trigger while the Designer is
   *  mounted (null on unmount) — the native File-menu Ctrl+S accelerator
   *  routes through this, same as the toolbar's "Save now" button. This
   *  document autosaves; the trigger just flushes the pending debounce. */
  registerSaveHandler?: (fn: (() => void) | null) => void;
}

/** What the canvas is currently showing instead of the live document. */
type Viewing =
  | { kind: 'issuance'; mi: ProjectManualIssuance }
  | { kind: 'snapshot'; snap: Issuance }
  | null;

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 1.6;
const ZOOM_KEY = 'designer.zoom';

/** Where the firm's default workspace arrangement lives (contract §2.9).
 *  ONE constant — swapping the app's out-of-box default for a different
 *  starting layout is a one-line change, exactly as the contract asks. */
export const DESIGNER_FIRM_LAYOUT_KEY = 'sf.designerDock.firmDefault.v2';

function SaveIndicator({ state, at }: { state: SaveState; at: Date | null }) {
  const label =
    state === 'saving'
      ? 'Saving…'
      : state === 'dirty'
        ? 'Unsaved changes'
        : state === 'error'
          ? 'Not saved — retrying'
          : at
            ? `Saved ${at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
            : 'Saved';
  return (
    <span
      className={`dsg-save dsg-save-${state}`}
      role="status"
      aria-live="polite"
      title="This document saves itself — there is nothing to submit"
    >
      {state === 'error' && (
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          aria-hidden="true"
        >
          <path d="M12 8v5M12 17h.01" />
          <circle cx="12" cy="12" r="9" />
        </svg>
      )}
      {label}
    </span>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className="dsg-icon-btn"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
    >
      {children}
    </button>
  );
}

/** Change bars + a delta: the universal "tracked changes" glyph. The
 *  highlighter is reserved for the manual marker pen (§2.4). */
const TrackChangesIcon = (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 3v18M7 3v18" />
    <path d="M16 7l5 9h-10z" />
  </svg>
);

export function Designer({
  projectSpecId,
  onOpenSection,
  onBack,
  onManageSections,
  userName,
  canEdit,
  canIssue,
  canSetFirmDefault = false,
  toast,
  isDark = false,
  userId,
  openFormatting,
  registerSaveHandler,
}: DesignerProps) {
  const doc = useDesignerDoc(projectSpecId, userName);
  const [editor, setEditor] = React.useState<Editor | null>(null);
  const topBarRef = React.useRef<HTMLElement>(null);
  const toolbarRef = React.useRef<HTMLDivElement>(null);
  const mayIssue = canIssue ?? canEdit;

  // ── Review surfaces ──
  const [deskOpen, setDeskOpen] = React.useState(false);
  const [exportOpen, setExportOpen] = React.useState(false);
  const [marksAnchor, setMarksAnchor] = React.useState<{
    left: number;
    bottom: number;
    right: number;
  } | null>(null);
  const [historyAnchor, setHistoryAnchor] = React.useState<{
    left: number;
    bottom: number;
    right: number;
  } | null>(null);
  const [viewing, setViewing] = React.useState<Viewing>(null);
  const [exportingIssuance, setExportingIssuance] = React.useState(false);
  const pendingRestoreRef = React.useRef<Issuance | null>(null);

  // Switching sections drops a per-section snapshot view; an ISSUANCE view
  // follows you across the rail — the whole manual as it was issued.
  React.useEffect(() => setViewing((v) => (v?.kind === 'snapshot' ? null : v)), [projectSpecId]);

  // A queued restore applies once the LIVE editor is back (setContent is a
  // normal transaction — it lands in the undo history and autosaves).
  React.useEffect(() => {
    if (!editor || viewing || !pendingRestoreRef.current) return;
    const snap = pendingRestoreRef.current;
    pendingRestoreRef.current = null;
    editor.commands.setContent(blocksToDoc(snap.snapshot?.coreContent || []), true);
    toast('Snapshot restored — press Ctrl+Z to undo');
  }, [editor, viewing, toast]);

  /* ─── UNIVERSAL UNDO (§2.7) ───
     One user-facing timeline over two histories. The coordinator records the
     ORDER actions happened in; Ctrl+Z asks whichever domain owns the most
     recent step. Nobody has to know there are two. */
  const undoTimeline = useUndoCoordinator();
  const noteFormaAction = undoTimeline.note;

  // The forma draft — zone and level edits land here, then write through the
  // existing model (project copy in place; master → copy-on-write fork).
  const draft = useFormaDraft(
    doc.forma,
    doc.spec ? { id: doc.spec.projectId, number: doc.projectNumber, name: doc.projectName } : null,
    userName,
    userId,
    (msg) => toast(msg, true),
    React.useCallback(() => noteFormaAction('forma'), [noteFormaAction])
  );
  // The paper always renders the newest formatting truth.
  const paperForma = draft.forma ?? doc.forma;

  // Token values follow the DRAFT: editing a placeholder binding in the
  // palette re-resolves it on the paper immediately. Stale placeholder ids
  // are stripped first, so unbinding one shows its unbound state again.
  const paperMeta = React.useMemo(() => {
    if (!paperForma) return doc.projectMeta;
    const meta = { ...doc.projectMeta };
    for (const id of usedPlaceholderIds(paperForma)) delete meta[id];
    Object.assign(
      meta,
      resolvePlaceholders(paperForma, {
        project: meta,
        issuances: doc.spec?.issuances || [],
        dateFormat: paperForma.docSettings?.dateFormat,
      })
    );
    Object.assign(meta, doc.spec?.tokenOverrides || {});
    return meta;
  }, [paperForma, doc.projectMeta, doc.spec]);

  /* ─── The two histories, joined ───
     The document's history lives in ProseMirror; the forma's lives in the
     draft. Each keeps its own — the coordinator only learns the ORDER. */
  React.useEffect(() => {
    undoTimeline.register('forma', { undo: draft.undo, redo: draft.redo });
    return () => undoTimeline.register('forma', null);
  }, [undoTimeline, draft.undo, draft.redo]);

  React.useEffect(() => {
    if (!editor) return;
    undoTimeline.register('doc', {
      undo: () => editor.chain().focus().undo().run(),
      redo: () => editor.chain().focus().redo().run(),
    });
    // A new step landing in ProseMirror's history IS a user action on the
    // document. Reading the depth is how we tell a real edit from a
    // selection move, an undo, or a decoration rebuild.
    let depth = undoDepth(editor.state);
    const onTx = () => {
      const next = undoDepth(editor.state);
      if (next > depth) undoTimeline.note('doc');
      depth = next;
    };
    editor.on('transaction', onTx);
    return () => {
      editor.off('transaction', onTx);
      undoTimeline.register('doc', null);
    };
  }, [editor, undoTimeline]);

  // A different section is a different document — and a different timeline.
  const resetTimeline = undoTimeline.reset;
  React.useEffect(() => resetTimeline(), [projectSpecId, resetTimeline]);

  /* Ctrl+Z / Ctrl+Y, once, for the whole surface. Captured before ProseMirror
     sees them so the caret being in the paper never means "only text undoes".
     Real form fields keep their own native undo — retyping in a text box is
     not a document action. */
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      const key = e.key.toLowerCase();
      const isUndo = key === 'z' && !e.shiftKey;
      const isRedo = key === 'y' || (key === 'z' && e.shiftKey);
      if (!isUndo && !isRedo) return;
      const target = e.target as HTMLElement | null;
      if (target && /^(input|textarea)$/i.test(target.tagName)) return;
      e.preventDefault();
      e.stopPropagation();
      if (isUndo) undoTimeline.undo();
      else undoTimeline.redo();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [undoTimeline]);

  /* ─── The workspace's panels: dropped down, docked, floating, or closed ───
     Josh's law lives here. Every panel is always one click from its top-bar
     button; docking is convenience. `openTool` is the ONE path every
     contextual entry point uses — clicking a band, right-click ▸ Level
     style… — and it never forces a dock. */
  const dockHandleRef = React.useRef<FormaDockHandle | null>(null);
  const [dockPanels, setDockPanels] = React.useState<string[]>([]);
  const [floatingTools, setFloatingTools] = React.useState<FloatingTool[]>([]);
  const [dropdown, setDropdown] = React.useState<{
    id: string;
    anchor: { left: number; bottom: number };
  } | null>(null);
  const [panelDragId, setPanelDragId] = React.useState<string | null>(null);

  // A panel the user dragged into the dock stops being a floating panel.
  React.useEffect(() => {
    setFloatingTools((f) => {
      const next = f.filter((t) => !dockPanels.includes(t.id));
      return next.length === f.length ? f : next;
    });
    setDropdown((d) => (d && dockPanels.includes(d.id) ? null : d));
  }, [dockPanels]);

  const floatTool = React.useCallback((id: string, at?: { left: number; top: number } | null) => {
    setDropdown((d) => (d?.id === id ? null : d));
    setFloatingTools((f) => (f.some((t) => t.id === id) ? f : [...f, { id, at }]));
  }, []);

  const openTool = React.useCallback(
    (id: string) => {
      if (dockPanels.includes(id)) {
        dockHandleRef.current?.showPanel(id);
        return;
      }
      floatTool(id);
    },
    [dockPanels, floatTool]
  );

  const closeFloating = React.useCallback(
    (id: string) => setFloatingTools((f) => f.filter((t) => t.id !== id)),
    []
  );

  const dockFloating = React.useCallback((id: string) => {
    setFloatingTools((f) => f.filter((t) => t.id !== id));
    dockHandleRef.current?.showPanel(id);
  }, []);

  /* CLICK a panel button: show the panel where it already lives, or drop it
     down anchored under the button. Clicking the button of an open dropdown
     closes it — a toggle, like every other button on this row. */
  const onPanelClick = React.useCallback(
    (id: string, anchor: { left: number; bottom: number }) => {
      if (dockPanels.includes(id)) {
        dockHandleRef.current?.showPanel(id);
        return;
      }
      if (floatingTools.some((t) => t.id === id)) {
        closeFloating(id);
        return;
      }
      setDropdown((d) => (d?.id === id ? null : { id, anchor }));
    },
    [dockPanels, floatingTools, closeFloating]
  );

  /* DRAG a panel button: into the dock it docks (FormaDock already accepts
     this payload); released outside every dock zone it floats where it
     landed. */
  const onPanelDragEnd = React.useCallback(
    (id: string, x: number, y: number) => {
      setPanelDragId(null);
      if (isOverDockZone(x, y)) return; // the dock took it
      floatTool(id, { left: x, top: y });
    },
    [floatTool]
  );

  /* A DOCKED panel dragged out of every dock zone becomes a floating box. */
  const undockToFloat = React.useCallback(
    (id: string, at: { left: number; top: number }) => {
      dockHandleRef.current?.hidePanel(id);
      floatTool(id, at);
    },
    [floatTool]
  );

  /* Publish this arrangement as the firm's default (§2.9). Admin-gated at
     the button AND at the server — this changes what every colleague who
     has never arranged their own workspace opens into, so it says so. */
  const setFirmDefault = React.useCallback(() => {
    const layout = dockHandleRef.current?.snapshotLayout();
    if (!layout) {
      toast('The workspace is still opening — try again in a moment.', true);
      return;
    }
    FirmPrefs.set(DESIGNER_FIRM_LAYOUT_KEY, layout, userName)
      .then(() =>
        toast('Saved as the firm default — everyone who has not customised will see this layout')
      )
      .catch((err: unknown) =>
        toast(
          'Could not save the firm default: ' +
            (err instanceof Error ? err.message : String(err ?? 'unknown')),
          true
        )
      );
  }, [toast, userName]);

  const panelEntries = React.useMemo<PanelEntry[]>(
    () =>
      DESIGNER_PALETTES.filter((p) => p.id !== 'canvas').map((p) => ({
        id: p.id,
        title: p.title,
        icon: PANEL_ICONS[p.id] ?? null,
        where: dockPanels.includes(p.id)
          ? 'docked'
          : floatingTools.some((t) => t.id === p.id)
            ? 'floating'
            : dropdown?.id === p.id
              ? 'dropdown'
              : null,
      })),
    [dockPanels, floatingTools, dropdown]
  );

  // ── Zone selection (ONE selection system: a band, or nothing) ──
  const [styleSel, setStyleSel] = React.useState<StyleSelection>(null);
  const onStyleSelect = React.useCallback(
    (sel: StyleSelection) => {
      setStyleSel(sel);
      // Clicking a band brings its editor forward — wherever that lives.
      if (sel?.kind === 'zone') openTool('zone');
    },
    [openTool]
  );

  // ── The Levels sub-tool ──
  // The palette follows the caret unless the user pointed it somewhere
  // deliberately. Only a deliberate pick lights the paper, so typing never
  // flashes highlights across the page.
  const [levelFocus, setLevelFocus] = React.useState<number | null>(null);
  const openLevelStyle = React.useCallback(
    (level: number) => {
      setLevelFocus(level);
      openTool('level-props');
    },
    [openTool]
  );

  // Which page variant the zone tools are editing; clamped to what the
  // forma actually has.
  const [pageVariant, setPageVariant] = React.useState<PageVariant>('odd');
  const pvs = pageVariants(paperForma);
  const effPageVariant = pvs.includes(pageVariant) ? pageVariant : 'odd';

  const [tokenDrag, setTokenDrag] = React.useState(false);
  const [dockLocked, setDockLocked] = React.useState<boolean>(() => {
    try {
      return localStorage.getItem('sf.designerDockLocked') !== '0';
    } catch {
      return true;
    }
  });
  const toggleDockLocked = React.useCallback(
    () =>
      setDockLocked((v) => {
        const nv = !v;
        try {
          localStorage.setItem('sf.designerDockLocked', nv ? '1' : '0');
        } catch {
          /* private mode */
        }
        return nv;
      }),
    []
  );

  // Opening a project forma from the Formas library: no mode to enter, just
  // bring the paper's tools forward.
  React.useEffect(() => {
    if (!openFormatting) return;
    openTool('zone');
    // Re-apply on every new request timestamp, not on object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFormatting?.ts]);

  // Esc clears the zone selection and the level focus (DESIGN §3.4 —
  // a selection is never sticky). The editor's own Esc still blurs it.
  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setStyleSel(null);
      setLevelFocus(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Remember the section last opened per project — opening this project's
  // FORMA later previews against it (contract §3.2: real content, always).
  React.useEffect(() => {
    const pid = doc.spec?.projectId;
    if (!pid) return;
    try {
      localStorage.setItem('sf.designer.lastSpec.' + pid, projectSpecId);
    } catch {
      /* private mode */
    }
  }, [doc.spec?.projectId, projectSpecId]);

  const [zoom, setZoom] = React.useState<number>(() => {
    const stored = Prefs.get(ZOOM_KEY) ?? Number(localStorage.getItem(ZOOM_KEY));
    const n = Number(stored);
    return Number.isFinite(n) && n >= ZOOM_MIN && n <= ZOOM_MAX ? n : 1;
  });
  // Re-render when prefs land from the server so the layout follows the user
  // across devices rather than being pinned to this machine's localStorage.
  React.useEffect(
    () =>
      Prefs.subscribe(() => {
        const z = Number(Prefs.get(ZOOM_KEY));
        if (Number.isFinite(z) && z >= ZOOM_MIN && z <= ZOOM_MAX) setZoom(z);
      }),
    []
  );

  const setZoomPersisted = (next: number) => {
    const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, Number(next.toFixed(2))));
    setZoom(clamped);
    Prefs.set(ZOOM_KEY, clamped);
    try {
      localStorage.setItem(ZOOM_KEY, String(clamped));
    } catch {
      /* see above */
    }
  };

  const onChange = React.useCallback(
    (blocks: ContentBlock[]) => doc.onBlocksChanged(blocks),
    [doc]
  );

  const onSave = React.useCallback(() => {
    void Promise.all([doc.flush(), draft.flush()]).then(() => toast('Saved'));
  }, [doc, draft, toast]);
  const onSaveRef = React.useRef(onSave);
  onSaveRef.current = onSave;
  React.useEffect(() => {
    registerSaveHandler?.(() => onSaveRef.current());
    return () => registerSaveHandler?.(null);
  }, [registerSaveHandler]);

  // Leaving flushes first — an edit made a second ago is already on the row.
  const leave = React.useCallback(async () => {
    await Promise.all([doc.flush(), draft.flush()]);
    onBack();
  }, [doc, draft, onBack]);

  const openSection = React.useCallback(
    async (id: string) => {
      if (id === projectSpecId) return;
      await Promise.all([doc.flush(), draft.flush()]);
      onOpenSection(id);
    },
    [doc, draft, onOpenSection, projectSpecId]
  );

  // Navigation ▸ a heading: scroll the paper to it and put the caret in it.
  const jumpTo = React.useCallback(
    (item: NavItem) => {
      if (!editor) return;
      editor
        .chain()
        .focus()
        .setTextSelection(item.pos + 1)
        .run();
      editor.view.dom
        .querySelector(`[data-block-id="${item.id}"]`)
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    },
    [editor]
  );

  // ── What the canvas shows while viewing history ──
  // For an issuance view: this section's frozen snapshot from that manual
  // issuance. undefined = the section was not part of it.
  const viewState = React.useMemo<{
    blocks: ContentBlock[] | null;
    key: string;
    label: string;
    missing?: boolean;
  } | null>(() => {
    if (!viewing) return null;
    if (viewing.kind === 'snapshot') {
      const s = viewing.snap;
      return {
        blocks: s.snapshot?.coreContent || [],
        key: `snap:${s.id}`,
        label: `Snapshot · ${new Date(s.createdAt || 0).toLocaleString([], {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}`,
      };
    }
    const mi = viewing.mi;
    const entry = mi.manifest.find((m) => m.specId === projectSpecId);
    const specRow = doc.exportCtx?.allSpecs.find((s) => s.id === projectSpecId);
    const iss = entry
      ? (specRow?.issuances || []).find((i) => i.id === entry.specIssuanceId)
      : undefined;
    return {
      blocks: iss?.snapshot?.coreContent || null,
      key: `iss:${mi.id}`,
      label: `${mi.name} · as issued`,
      missing: !iss?.snapshot,
    };
  }, [viewing, projectSpecId, doc.exportCtx]);

  /* ── Export an issuance exactly as it existed ──
     Used both from the timeline ("Export…" on a past issuance) and as the
     file half of the issue flow itself (§2.10). `fresh` carries the rows
     that were just frozen, so issuing does not have to wait for the reload
     to land in state before it can produce the documents. */
  const exportIssuance = React.useCallback(
    async (
      mi: ProjectManualIssuance,
      opts?: { fresh?: ProjectSpec[]; format?: ExportFormat; marked?: boolean }
    ) => {
      if (!doc.exportCtx) {
        toast('Still loading the project — try again in a moment.', true);
        return;
      }
      setExportingIssuance(true);
      try {
        // Freshly-issued rows supersede whatever the loaded context holds.
        const pool = opts?.fresh?.length
          ? [
              ...opts.fresh,
              ...doc.exportCtx.allSpecs.filter((s) => !opts.fresh!.some((f) => f.id === s.id)),
            ]
          : doc.exportCtx.allSpecs;
        const overrides = new Map<
          string,
          {
            coreContent: ContentBlock[];
            systems?: any[];
            tokenOverrides?: Record<string, string>;
            csiNumber?: string;
            title?: string;
          }
        >();
        const targets: ProjectSpec[] = [];
        for (const entry of mi.manifest) {
          const spec = pool.find((s) => s.id === entry.specId);
          const iss = spec
            ? (spec.issuances || []).find((i) => i.id === entry.specIssuanceId)
            : undefined;
          if (!spec || !iss?.snapshot) continue;
          // Marked export: the diff baseline is the issuance BEFORE this one,
          // so hide the one we are exporting from the row the pipeline reads.
          // (Content still comes from the snapshot override below, so what
          //  prints is exactly what was sealed.)
          targets.push(
            opts?.marked
              ? { ...spec, issuances: (spec.issuances || []).filter((i) => i.id !== iss.id) }
              : spec
          );
          overrides.set(spec.id, {
            coreContent: iss.snapshot.coreContent || [],
            systems: iss.snapshot.systems || [],
            tokenOverrides: iss.snapshot.tokenOverrides,
            csiNumber: iss.snapshot.csiNumber,
            title: iss.snapshot.title,
          });
        }
        if (!targets.length) {
          toast('No snapshots survive for that issuance.', true);
          return;
        }
        const res = await exportManualSpecs({
          specs: targets,
          ctx: { ...doc.exportCtx, allSpecs: pool },
          marked: !!opts?.marked,
          format: opts?.format ?? 'docx',
          isWeb,
          snapshotOverrides: overrides,
          issuanceOverride: { name: mi.name, date: mi.date },
        });
        if (res.kind === 'blocked') {
          toast(
            `Set ${res.placeholders.join(', ')} in the forma's Project Tokens before exporting.`,
            true
          );
        } else if (res.kind === 'done') {
          if (res.failures.length) {
            toast(`Exported with ${res.failures.length} failure(s) — see console.`, true);
            console.warn('[designer-export-issuance] failures:', res.failures);
          } else {
            toast(`Exported “${mi.name}” — ${res.ok} file${res.ok === 1 ? '' : 's'}`);
          }
        }
      } catch (err) {
        toast(
          'Export failed: ' + (err instanceof Error ? err.message : String(err ?? 'unknown')),
          true
        );
      } finally {
        setExportingIssuance(false);
      }
    },
    [doc.exportCtx, toast]
  );

  /* ── Issue = RECORD **and** EXPORT (§2.10) ──
     The record goes down first: it is the thing that must not be lost, and a
     set that was recorded but not exported can still be exported from the
     timeline. A set that was exported but never recorded cannot be
     un-shipped. */
  const issueAndExport = React.useCallback(
    async (
      meta: { name: string; date?: string; notes?: string },
      sectionIds: string[],
      options: IssueExportOptions
    ) => {
      const { issuance, specs } = await doc.issueManual(meta, sectionIds);
      toast(
        `Issued “${issuance.name}” — ${sectionIds.length} section${
          sectionIds.length === 1 ? '' : 's'
        }. Saving the files…`
      );
      await exportIssuance(issuance, {
        fresh: specs,
        format: options.format,
        marked: options.marked,
      });
    },
    [doc, exportIssuance, toast]
  );

  const spec = doc.spec;

  /* ─── Row 2: the toolbar ───
     CORE items can never be unpinned; everything else the user pins and
     unpins from Workspace ▸ Customise toolbar… or by right-clicking the row. */
  const toolbarItems = React.useMemo<ToolbarItem[]>(() => {
    if (!spec) return [];
    const editable = canEdit && !viewState;
    return [
      {
        id: 'undo-redo',
        label: 'Undo and redo',
        core: true,
        node: <UniversalHistory timeline={undoTimeline} editor={editor} />,
      },
      {
        id: 'zoom',
        label: 'Zoom',
        core: true,
        node: (
          <div className="dsg-group" role="group" aria-label="Zoom">
            <IconButton
              label="Zoom out"
              onClick={() => setZoomPersisted(zoom - 0.1)}
              disabled={zoom <= ZOOM_MIN}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M8 11h6M20 20l-3.5-3.5" />
              </svg>
            </IconButton>
            <button
              type="button"
              className="dsg-zoom-value"
              onClick={() => setZoomPersisted(1)}
              title="Reset zoom to 100%"
            >
              {Math.round(zoom * 100)}%
            </button>
            <IconButton
              label="Zoom in"
              onClick={() => setZoomPersisted(zoom + 0.1)}
              disabled={zoom >= ZOOM_MAX}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="7" />
                <path d="M8 11h6M11 8v6M20 20l-3.5-3.5" />
              </svg>
            </IconButton>
          </div>
        ),
      },
      {
        id: 'save',
        label: 'Save now',
        core: true,
        node: (
          <button type="button" className="dsg-primary" onClick={onSave} disabled={!editable}>
            Save now
          </button>
        ),
      },
      {
        id: 'level',
        label: 'Paragraph level',
        node: <LevelCluster editor={editor} canEdit={editable} onLevelStyle={openLevelStyle} />,
      },
      {
        id: 'marks',
        label: 'Bold, italic, underline, highlight',
        node: <InlineMarks editor={editor} canEdit={editable} />,
      },
      {
        id: 'find',
        label: 'Find & replace',
        node: <FindButton editor={editor} />,
      },
      {
        id: 'insert',
        label: 'Insert image and table',
        node: (
          <div className="dsg-group" role="group" aria-label="Insert">
            <InsertImageButton editor={editor} canEdit={editable} />
            <InsertTableButton editor={editor} canEdit={editable} />
          </div>
        ),
      },
      {
        id: 'smart-block',
        label: 'Smart block',
        node: <SmartBlockPicker editor={editor} canEdit={editable} spec={spec} />,
      },
      {
        // Ctrl+Z now covers formatting too (§2.7); this pair stays as the
        // visual affordance for "step back through FORMATTING only",
        // which is still a thing people reach for while restyling.
        id: 'forma-history',
        label: 'Undo and redo formatting only',
        node: (
          <div className="dsg-group" role="group" aria-label="Formatting history">
            <IconButton
              label="Undo formatting change only"
              onClick={() => void draft.undo()}
              disabled={!draft.canUndo}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 7v6h6" />
                <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
                <path d="M9 20h9" />
              </svg>
            </IconButton>
            <IconButton
              label="Redo formatting change only"
              onClick={() => void draft.redo()}
              disabled={!draft.canRedo}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M21 7v6h-6" />
                <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
                <path d="M6 20h9" />
              </svg>
            </IconButton>
          </div>
        ),
      },
      {
        id: 'review',
        label: 'History and track changes',
        node: (
          <div className="dsg-group" role="group" aria-label="Review">
            <button
              type="button"
              className="dsg-icon-btn"
              aria-label="Snapshots and history"
              title="Snapshots and history"
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setHistoryAnchor({ left: r.left, bottom: r.bottom, right: r.right });
              }}
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M3 3v5h5" />
                <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
                <path d="M12 7v5l4 2" />
              </svg>
            </button>
            <button
              type="button"
              className="dsg-icon-btn"
              aria-label="Track changes — revision marks"
              title="Track changes — how revision marks print"
              onClick={(e) => {
                const r = e.currentTarget.getBoundingClientRect();
                setMarksAnchor({ left: r.left, bottom: r.bottom, right: r.right });
              }}
            >
              {TrackChangesIcon}
            </button>
          </div>
        ),
      },
      {
        id: 'export',
        label: 'Export…',
        node: (
          <button
            type="button"
            className="dsg-btn"
            onClick={() => setExportOpen(true)}
            disabled={!doc.exportCtx}
          >
            <Download size={14} aria-hidden="true" />
            Export…
          </button>
        ),
      },
      {
        id: 'issue',
        label: doc.phase === 'design' ? 'Issue…' : 'Issuances…',
        node: (
          <button
            type="button"
            className="dsg-btn"
            onClick={() => setDeskOpen(true)}
            title="Issuances — the manual's baselines and addenda"
          >
            <Stamp size={14} aria-hidden="true" />
            {doc.phase === 'design' ? 'Issue…' : 'Issuances…'}
          </button>
        ),
      },
    ];
  }, [
    spec,
    editor,
    canEdit,
    viewState,
    zoom,
    onSave,
    openLevelStyle,
    undoTimeline,
    draft.undo,
    draft.redo,
    draft.canUndo,
    draft.canRedo,
    doc.exportCtx,
    doc.phase,
  ]);

  if (doc.loading) {
    return (
      <div className="dsg-root dsg-loading" aria-busy="true">
        <div className="dsg-skeleton-rail" />
        <div className="dsg-skeleton-sheet">
          <span className="sr-only">Opening the spec…</span>
        </div>
      </div>
    );
  }

  if (doc.error || !spec) {
    return (
      <div className="dsg-root dsg-error-root">
        <EmptyState
          title="This spec could not be opened"
          subtitle={doc.error || 'It may have been removed from the project.'}
          action={{ label: 'Back to the project', onClick: onBack }}
        />
      </div>
    );
  }

  // The breadcrumb shows the section number the way this forma prints it.
  const csi =
    formatSpecNumber(spec.csiNumber || '', doc.forma?.docSettings?.specNumberFormat) ||
    spec.csiNumber ||
    '—';

  const paperEditable = canEdit && !viewState;

  return (
    <div className="dsg-root">
      <header className="dsg-topbar" ref={topBarRef}>
        {/* ── Row 1: identity. Where am I, and what state is this document
            in. Nothing else lives here. ── */}
        <div className="dsg-idrow">
          <button type="button" className="dsg-back" onClick={() => void leave()}>
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Projects
          </button>
          <nav className="dsg-crumb" aria-label="Breadcrumb">
            <span className="dsg-crumb-project">
              {doc.projectNumber ? `${doc.projectNumber} · ` : ''}
              {doc.projectName || 'Project'}
            </span>
            <span className="dsg-crumb-sep" aria-hidden="true">
              ▸
            </span>
            <span className="dsg-crumb-spec">
              <strong>{csi}</strong> {spec.title || 'Untitled section'}
            </span>
          </nav>

          <div className="dsg-idrow-status">
            {/* Which life the document is living. */}
            <span
              className={`dsg-mode-chip dsg-mode-${doc.phase}`}
              title={
                doc.phase === 'design'
                  ? 'Not yet issued — edits are quiet; snapshots keep them recoverable'
                  : doc.baseline
                    ? `Changes since “${doc.baseline.name}” render as revision marks`
                    : 'Issued — this section was added after the current issuance'
              }
            >
              {doc.phase === 'design' ? 'Design phase' : doc.baseline?.name || 'Issued'}
            </span>
            {/* Whose forma the paper's formatting lands on. */}
            <span
              className={`dsg-scope-chip${draft.isProjectCopy ? ' is-project' : ''}`}
              title={
                draft.isProjectCopy
                  ? 'This project has its own forma copy — formatting edits apply to this project only'
                  : 'This spec still prints with a master forma. Your first formatting edit forks a copy for this project; the master stays untouched.'
              }
            >
              {draft.isProjectCopy ? 'Project forma' : 'Master forma'}
            </span>
            {(draft.dirty || draft.saving) && (
              <span className="dsg-save" role="status" aria-live="polite">
                {draft.saving ? 'Saving formatting…' : 'Formatting edits pending'}
              </span>
            )}
            <SaveIndicator state={doc.saveState} at={doc.lastSavedAt} />
          </div>
        </div>

        {/* ── Row 2: the panel buttons, the tools, the Workspace menu. ── */}
        <div ref={toolbarRef}>
          <DesignerToolbar
            items={toolbarItems}
            panels={panelEntries}
            onPanelClick={onPanelClick}
            onPanelDragStart={setPanelDragId}
            onPanelDragEnd={onPanelDragEnd}
            dockLocked={dockLocked}
            onToggleDockLocked={toggleDockLocked}
            onResetLayout={() => dockHandleRef.current?.resetLayout()}
            onSetFirmDefault={setFirmDefault}
            canSetFirmDefault={canSetFirmDefault}
            userId={userId}
          />
        </div>
      </header>

      <div className="dsg-body">
        <main className="dsg-main">
          {!canEdit && !viewState && (
            <div className="dsg-readonly-banner" role="status">
              You have view access to this spec. Ask a project publisher for edit rights to make
              changes.
            </div>
          )}
          {viewState && (
            <div className="dsg-viewing-banner" role="status">
              <span className="dsg-viewing-label">{viewState.label} — read-only</span>
              <span className="dsg-viewing-actions">
                {viewing?.kind === 'snapshot' && canEdit && (
                  <button
                    type="button"
                    className="dsg-btn"
                    onClick={() => {
                      pendingRestoreRef.current = viewing.snap;
                      setViewing(null);
                    }}
                  >
                    Restore this version
                  </button>
                )}
                {viewing?.kind === 'issuance' && (
                  <button
                    type="button"
                    className="dsg-btn"
                    disabled={exportingIssuance}
                    onClick={() => void exportIssuance(viewing.mi)}
                  >
                    {exportingIssuance ? 'Exporting…' : 'Export as issued'}
                  </button>
                )}
                <button type="button" className="dsg-btn" onClick={() => setViewing(null)}>
                  Back to the live document
                </button>
              </span>
            </div>
          )}

          <DesignerWorkspace
            isDark={isDark}
            userId={userId || 'anon'}
            locked={dockLocked}
            doc={doc}
            draft={draft}
            canEdit={paperEditable}
            sections={doc.sections}
            activeSectionId={projectSpecId}
            onOpenSection={(id) => void openSection(id)}
            onManageSections={onManageSections}
            editor={editor}
            onJump={jumpTo}
            styleSel={styleSel}
            onStyleSelect={onStyleSelect}
            levelFocus={levelFocus}
            onLevelFocus={setLevelFocus}
            pageVariant={effPageVariant}
            onPageVariant={setPageVariant}
            onDragState={setTokenDrag}
            dragActive={tokenDrag}
            toast={toast}
            floatingTools={floatingTools}
            onCloseFloating={closeFloating}
            onDockFloating={dockFloating}
            dropdown={dropdown}
            onCloseDropdown={() => setDropdown(null)}
            onFloatDropdown={floatTool}
            onUndock={undockToFloat}
            panelDragId={panelDragId}
            firmLayoutKey={DESIGNER_FIRM_LAYOUT_KEY}
            onDockPanelsChange={setDockPanels}
            onDockReady={(h) => {
              dockHandleRef.current = h;
            }}
            canvas={(wiring) =>
              viewState?.missing ? (
                <div className="dsg-viewing-missing">
                  <EmptyState
                    title="Not part of this issuance"
                    subtitle={`${csi} ${spec.title || ''} was added to the manual after ${
                      viewing?.kind === 'issuance' ? viewing.mi.name : 'this issuance'
                    }. Pick another section in the rail, or go back to the live document.`}
                    action={{
                      label: 'Back to the live document',
                      onClick: () => setViewing(null),
                    }}
                  />
                </div>
              ) : (
                <DesignerCanvas
                  specId={viewState ? `${projectSpecId}:${viewState.key}` : projectSpecId}
                  blocks={viewState ? viewState.blocks || [] : spec.coreContent || []}
                  forma={viewState ? doc.forma : paperForma}
                  specNumber={spec.csiNumber || ''}
                  specName={spec.title || ''}
                  projectMeta={viewState ? doc.projectMeta : paperMeta}
                  zoom={zoom}
                  onChange={viewState ? () => undefined : onChange}
                  onSave={onSave}
                  onEscape={() => topBarRef.current?.querySelector('button')?.focus()}
                  onEditorReady={setEditor}
                  editable={paperEditable}
                  // The bands are live objects whenever the paper is live.
                  design={paperEditable ? wiring : undefined}
                  onLevelStyle={openLevelStyle}
                  baselineBlocks={viewState ? null : doc.baselineBlocks}
                  revisionSettings={doc.revisionSettings}
                />
              )
            }
          />
        </main>
      </div>

      {/* The find bar hangs off the toolbar, whether Ctrl+F or the button
          opened it — so it is never unreachable because a button is unpinned. */}
      <FindReplaceHost editor={editor} canEdit={paperEditable} anchorRef={toolbarRef} />

      {/* ── Review overlays ── */}
      {marksAnchor && (
        <RevisionStylesPanel
          anchor={marksAnchor}
          settings={doc.revisionSettings}
          baseline={doc.baseline}
          baselineLabel={doc.baseline?.name}
          canEdit={canEdit}
          onChange={(next) =>
            void doc
              .saveRevisionSettings(next)
              .catch(() => toast('Could not save mark styles', true))
          }
          onClose={() => setMarksAnchor(null)}
        />
      )}
      {historyAnchor && (
        <HistoryPanel
          anchor={historyAnchor}
          spec={spec}
          canEdit={canEdit}
          onView={(snap) => setViewing({ kind: 'snapshot', snap })}
          onRestore={(snap) => {
            pendingRestoreRef.current = snap;
            setViewing(null);
          }}
          onClose={() => setHistoryAnchor(null)}
        />
      )}
      <IssuanceDesk
        open={deskOpen}
        onClose={() => setDeskOpen(false)}
        projectName={doc.projectName}
        specs={doc.exportCtx?.allSpecs || []}
        manualIssuances={doc.manualIssuances}
        exportCtx={doc.exportCtx}
        canIssue={mayIssue}
        busy={exportingIssuance}
        onIssue={issueAndExport}
        onViewIssuance={(mi) => {
          setDeskOpen(false);
          setViewing({ kind: 'issuance', mi });
        }}
        onExportIssuance={(mi) => void exportIssuance(mi)}
        toast={toast}
      />
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        currentSpecId={projectSpecId}
        specs={doc.exportCtx?.allSpecs || []}
        exportCtx={doc.exportCtx}
        hasBaseline={doc.phase === 'issued'}
        toast={toast}
      />
    </div>
  );
}

export default Designer;
